<template>
    <div
        class="overflow-hidden rounded-xl border border-zinc-200 bg-white dark:border-gray-700 dark:bg-gray-800"
    >
        <div
            class="flex flex-wrap items-center gap-4 border-b border-zinc-200 px-[18px] py-4 dark:border-gray-700"
        >
            <div class="flex min-w-[220px] flex-1 items-center gap-3">
                <div
                    class="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full"
                    :style="{ background: scoreRing }"
                >
                    <div
                        class="flex h-[42px] w-[42px] items-center justify-center rounded-full bg-white text-[15px] font-semibold tabular-nums text-zinc-950 dark:bg-gray-800 dark:text-white"
                    >
                        {{ loading ? "–" : checklistData.final_score || 0 }}
                    </div>
                </div>
                <div class="flex flex-col gap-0.5">
                    <div class="flex items-center gap-2">
                        <span
                            class="text-[15px] font-semibold text-zinc-950 dark:text-white"
                        >
                            Checklist de verificação
                        </span>
                        <span
                            v-if="!loading && !error"
                            class="rounded-full px-2 py-px text-xs font-medium"
                            :class="scoreTone.badge"
                        >
                            {{ scoreTone.label }}
                        </span>
                    </div>
                    <span class="text-[13px] text-zinc-500 dark:text-gray-400">
                        <template v-if="loading"
                            >Carregando checklist…</template
                        >
                        <template v-else>
                            {{ passedCount }}/{{ totalCount }} pontos aprovados
                            <template v-if="checklistData.total_orders">
                                · {{ checklistData.total_orders }} pedidos
                                analisados
                            </template>
                        </template>
                    </span>
                </div>
            </div>
            <div
                class="flex gap-0.5 rounded-lg bg-zinc-100 p-[3px] dark:bg-gray-700"
            >
                <button
                    type="button"
                    class="h-7 rounded-md px-2.5 text-[12.5px] font-medium transition-colors"
                    :class="segmentClass(filterStatus === 'issues')"
                    @click="filterStatus = 'issues'"
                >
                    Problemas ({{ issuesCount }})
                </button>
                <button
                    type="button"
                    class="h-7 rounded-md px-2.5 text-[12.5px] font-medium transition-colors"
                    :class="segmentClass(filterStatus === 'all')"
                    @click="filterStatus = 'all'"
                >
                    Todos ({{ totalCount }})
                </button>
            </div>
        </div>

        <div
            v-if="error"
            class="flex items-center justify-between gap-3 px-[18px] py-5 text-[13px] text-red-700 dark:text-red-400"
        >
            <span>{{ error }}</span>
            <button
                type="button"
                class="inline-flex h-7 items-center gap-1.5 rounded-md border border-zinc-200 px-2.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                @click="loadChecklist"
            >
                <ArrowPathIcon class="h-3.5 w-3.5" />
                Tentar de novo
            </button>
        </div>

        <template v-else-if="!loading">
            <div
                v-if="severityBreakdown.length"
                class="grid border-b border-zinc-200 dark:border-gray-700"
                :style="{
                    gridTemplateColumns: `repeat(${severityBreakdown.length}, minmax(0, 1fr))`,
                }"
            >
                <div
                    v-for="level in severityBreakdown"
                    :key="level.key"
                    class="flex flex-col gap-1.5 border-r border-zinc-100 px-3.5 py-2.5 last:border-r-0 dark:border-gray-700/60"
                >
                    <span
                        class="whitespace-nowrap text-xs text-zinc-500 dark:text-gray-400"
                    >
                        {{ level.label }}
                    </span>
                    <div class="flex items-center gap-2">
                        <span
                            class="text-[13.5px] font-semibold tabular-nums"
                            :class="
                                level.ok < level.total
                                    ? level.tone.text
                                    : 'text-zinc-950 dark:text-white'
                            "
                        >
                            {{ level.ok }}/{{ level.total }}
                        </span>
                        <div
                            class="h-1 flex-1 overflow-hidden rounded-full bg-zinc-100 dark:bg-gray-700"
                        >
                            <div
                                class="h-full"
                                :class="
                                    level.ok < level.total
                                        ? level.tone.bar
                                        : 'bg-zinc-400 dark:bg-gray-500'
                                "
                                :style="{
                                    width: (level.ok / level.total) * 100 + '%',
                                }"
                            ></div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex flex-col">
                <div
                    v-for="check in visibleChecks"
                    :key="check.id"
                    class="grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-3 border-b border-zinc-100 px-[18px] py-3 dark:border-gray-700/60"
                >
                    <span
                        class="mt-px whitespace-nowrap rounded-[5px] px-[7px] py-px text-[11.5px] font-medium"
                        :class="
                            check.status === 'passed'
                                ? 'bg-zinc-100 text-zinc-600 dark:bg-gray-700 dark:text-gray-300'
                                : toneFor(check).chip
                        "
                    >
                        {{
                            check.status === "passed"
                                ? "OK"
                                : check.risk_level || "Alerta"
                        }}
                    </span>
                    <div class="flex min-w-0 flex-col gap-0.5">
                        <span
                            class="text-[13.5px] font-medium text-zinc-950 dark:text-white"
                        >
                            {{ check.label }}
                        </span>
                        <span
                            v-if="check.description"
                            class="text-[12.5px] text-zinc-500 dark:text-gray-400"
                        >
                            {{ check.description }}
                        </span>
                    </div>
                    <div
                        v-if="
                            check.current_value !== undefined ||
                            check.expected_value
                        "
                        class="flex flex-col items-end gap-0.5 whitespace-nowrap font-mono text-xs"
                    >
                        <span
                            v-if="check.current_value !== undefined"
                            class="font-medium"
                            :class="
                                check.status === 'passed'
                                    ? 'text-zinc-600 dark:text-gray-300'
                                    : toneFor(check).text
                            "
                        >
                            {{ formatCheckValue(check.current_value) }}
                        </span>
                        <span
                            v-if="check.expected_value"
                            class="text-zinc-400 dark:text-gray-500"
                        >
                            esperado {{ check.expected_value }}
                        </span>
                    </div>
                </div>

                <div
                    v-if="filterStatus === 'issues' && issuesCount === 0"
                    class="flex items-center gap-2.5 px-[18px] py-5 text-[13.5px] text-green-700 dark:text-green-400"
                >
                    <CheckIcon class="h-4 w-4" />
                    <template v-if="totalCount">
                        Todos os {{ totalCount }} pontos de controle passaram.
                    </template>
                    <template v-else>
                        Nenhum ponto de controle retornado para este saque.
                    </template>
                </div>
                <div
                    v-else-if="filterStatus === 'issues' && passedCount > 0"
                    class="px-[18px] py-2.5 text-[12.5px] text-zinc-500 dark:text-gray-400"
                >
                    + {{ passedCount }} pontos aprovados sem ressalvas
                </div>
            </div>
        </template>

        <div v-else class="flex justify-center py-8">
            <ticto-loading />
        </div>
    </div>
</template>

<script>
import axios from "axios";
import TictoLoading from "../../layouts/TictoLoading.vue";
import { ArrowPathIcon, CheckIcon } from "@heroicons/vue/24/outline";

const SEVERITY_ORDER = [
    "critico",
    "alto",
    "medio",
    "baixo",
    "positivo",
    "outros",
];

const SEVERITY_TONES = {
    critico: {
        chip: "bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400",
        text: "text-red-700 dark:text-red-400",
        bar: "bg-red-700 dark:bg-red-400",
    },
    alto: {
        chip: "bg-orange-50 text-orange-700 dark:bg-orange-500/10 dark:text-orange-400",
        text: "text-orange-700 dark:text-orange-400",
        bar: "bg-orange-700 dark:bg-orange-400",
    },
    medio: {
        chip: "bg-yellow-50 text-yellow-700 dark:bg-yellow-500/10 dark:text-yellow-400",
        text: "text-yellow-700 dark:text-yellow-400",
        bar: "bg-yellow-700 dark:bg-yellow-400",
    },
    baixo: {
        chip: "bg-zinc-100 text-zinc-600 dark:bg-gray-700 dark:text-gray-300",
        text: "text-zinc-600 dark:text-gray-300",
        bar: "bg-zinc-600 dark:bg-gray-300",
    },
    positivo: {
        chip: "bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400",
        text: "text-green-700 dark:text-green-400",
        bar: "bg-green-700 dark:bg-green-400",
    },
    outros: {
        chip: "bg-zinc-100 text-zinc-600 dark:bg-gray-700 dark:text-gray-300",
        text: "text-zinc-600 dark:text-gray-300",
        bar: "bg-zinc-600 dark:bg-gray-300",
    },
};

export default {
    name: "CashoutChecklist",
    components: {
        TictoLoading,
        ArrowPathIcon,
        CheckIcon,
    },
    props: {
        cashoutId: {
            type: [String, Number],
            required: true,
        },
    },
    emits: ["loaded"],
    data() {
        return {
            loading: false,
            error: null,
            filterStatus: "issues",
            checklistData: {
                final_score: 0,
                total_orders: 0,
                status: "pending",
                checks: [],
                recommendation: "",
            },
        };
    },
    computed: {
        checks() {
            return this.checklistData.checks || [];
        },
        totalCount() {
            return this.checks.length;
        },
        passedCount() {
            return this.checks.filter((check) => check.status === "passed")
                .length;
        },
        issuesCount() {
            return this.totalCount - this.passedCount;
        },
        scoreTone() {
            const score = this.checklistData.final_score || 0;

            if (score >= 85) {
                return {
                    label: "Baixo risco",
                    color: "#22c55e",
                    badge: "bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400",
                };
            }

            if (score >= 70) {
                return {
                    label: "Risco moderado",
                    color: "#f59e0b",
                    badge: "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400",
                };
            }

            return {
                label: "Alto risco",
                color: "#ef4444",
                badge: "bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400",
            };
        },
        scoreRing() {
            const score = this.loading
                ? 0
                : this.checklistData.final_score || 0;
            const degrees = Math.round(Math.min(100, Math.max(0, score)) * 3.6);

            return `conic-gradient(${this.scoreTone.color} ${degrees}deg, rgba(161, 161, 170, 0.25) 0)`;
        },
        severityBreakdown() {
            const levels = {};

            this.checks.forEach((check) => {
                const key = this.severityKey(check.risk_level);

                if (!levels[key]) {
                    levels[key] = {
                        key,
                        label: check.risk_level || "Outros",
                        tone: SEVERITY_TONES[key],
                        ok: 0,
                        total: 0,
                    };
                }

                levels[key].total += 1;

                if (check.status === "passed") {
                    levels[key].ok += 1;
                }
            });

            return SEVERITY_ORDER.filter((key) => levels[key]).map(
                (key) => levels[key],
            );
        },
        visibleChecks() {
            const statusOrder = { failed: 0, warning: 1, passed: 2 };
            const list =
                this.filterStatus === "all"
                    ? this.checks
                    : this.checks.filter((check) => check.status !== "passed");

            return [...list].sort((a, b) => {
                const statusDiff =
                    (statusOrder[a.status] ?? 1) - (statusOrder[b.status] ?? 1);

                if (statusDiff !== 0) {
                    return statusDiff;
                }

                return (
                    SEVERITY_ORDER.indexOf(this.severityKey(a.risk_level)) -
                    SEVERITY_ORDER.indexOf(this.severityKey(b.risk_level))
                );
            });
        },
    },
    watch: {
        cashoutId() {
            this.filterStatus = "issues";
            this.loadChecklist();
        },
    },
    mounted() {
        this.loadChecklist();
    },
    methods: {
        async loadChecklist() {
            const cashoutId = this.cashoutId;
            this.loading = true;
            this.error = null;

            try {
                const { data } = await axios.get(
                    `/api/cashout/${cashoutId}/checklist`,
                );

                if (cashoutId !== this.cashoutId) {
                    return;
                }

                data.checks = (
                    Array.isArray(data.checks) ? data.checks : []
                ).map((check, index) => ({
                    ...check,
                    id: check.type || check.id || index,
                    label: check.title || check.label,
                    status: this.mapApiStatus(check.status),
                }));

                if (data.error) {
                    this.error =
                        "Checklist indisponível no momento. Tente de novo em instantes.";
                }

                this.checklistData = data;
                this.$emit("loaded", data);
            } catch (error) {
                if (cashoutId !== this.cashoutId) {
                    return;
                }

                console.error("Erro ao carregar checklist:", error);
                this.error =
                    error.response?.data?.message ||
                    "Erro ao carregar checklist. Tente novamente.";
                this.$emit("loaded", null);
            } finally {
                if (cashoutId === this.cashoutId) {
                    this.loading = false;
                }
            }
        },
        mapApiStatus(status) {
            switch (status) {
                case "approved":
                    return "passed";
                case "review":
                    return "warning";
                case "failed":
                case "rejected":
                    return "failed";
                default:
                    return status || "warning";
            }
        },
        severityKey(riskLevel) {
            const normalized = String(riskLevel || "")
                .toLowerCase()
                .normalize("NFD")
                .replace(/[̀-ͯ]/g, "");

            if (normalized.includes("critic")) return "critico";
            if (normalized.includes("alto")) return "alto";
            if (normalized.includes("medio")) return "medio";
            if (normalized.includes("baixo")) return "baixo";
            if (normalized.includes("positiv")) return "positivo";

            return "outros";
        },
        toneFor(check) {
            return SEVERITY_TONES[this.severityKey(check.risk_level)];
        },
        segmentClass(active) {
            return active
                ? "bg-white text-zinc-950 shadow-sm dark:bg-gray-800 dark:text-white"
                : "text-zinc-500 hover:text-zinc-700 dark:text-gray-400 dark:hover:text-gray-200";
        },
        formatCheckValue(value) {
            if (typeof value === "boolean") {
                return value ? "true" : "false";
            }

            if (typeof value === "number") {
                return value.toLocaleString("pt-BR", {
                    maximumFractionDigits: 2,
                });
            }

            return value;
        },
    },
};
</script>
